import React, { useContext, useEffect, useMemo, useState } from "react";
import Breadcrumb from "../../../../components/bootstrap/Breadcrumb";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardBody } from "../../../../components/bootstrap/Card";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Select from "../../../../components/bootstrap/forms/Select";
import Dropdown, { DropdownMenu, DropdownToggle } from "../../../../components/bootstrap/Dropdown";
import TableCustom from "../../../../components/MyCustom/TableCustom";
import Page from "../../../../layout/Page/Page";
import PageWrapper from "../../../../layout/PageWrapper/PageWrapper";
import SubHeader, { SubHeaderLeft, SubHeaderRight } from "../../../../layout/SubHeader/SubHeader";
import { demoPages } from "../../../../menu";
import { DefaultContext } from "../../../../contexts/default";
import EverestLogDB from "../../../../database/wrappers/everestLog";
import { limit, orderBy, where } from "firebase/firestore";
import masks from "../../../../helpers/utils/masks";
import api from "../../../../services/api";
import PreAlert from "../../../../helpers/utils/preAlert";

const Everest = () => {

    const { estabSelected, establishments, onShowAlert } = useContext(DefaultContext);

    const [everestLogs, setEverestLogs] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedEstab, setSelectedEstab] = useState('');
    const [selectedStore, setSelectedStore] = useState('');
    const [filterMenu, setFilterMenu] = useState(false);
    const [onlyFailed, setOnlyFailed] = useState(false);
    const [loadingResend, setLoadingResend] = useState(null);

    useEffect(() => {
        if (estabSelected) {
            setSelectedEstab(estabSelected);
        }
    }, [estabSelected]);

    useEffect(() => {
        const estab = selectedEstab || estabSelected;
        if (!estab) return;
        const db = new EverestLogDB();

        const constraints = [where('estabId', '==', estab)];
        if (selectedStore) {
            constraints.push(where('storeId', '==', selectedStore));
        }
        constraints.push(orderBy("date", 'desc'), limit(100));
        const unsubscribe = db.on(setEverestLogs, ...constraints);
        return () => unsubscribe && unsubscribe();
    }, [estabSelected, selectedEstab, selectedStore]);

    const filteredLogs = useMemo(() => {
        let logs = everestLogs;

        if (onlyFailed) {
            logs = logs.filter(item => item.success === false);
        }

        if (selectedStore) {
            logs = logs.filter(item => item.storeId === selectedStore);
        }

        return logs;
    }, [everestLogs, selectedDate, onlyFailed, selectedStore]);

    const optionsEstablishments = useMemo(
        () => establishments.map(item => ({ value: item.id, text: item.name })),
        [establishments]
    );

    const optionsStores = useMemo(() => {
        const byId = new Map();
        everestLogs.forEach(item => {
            if (!item?.storeId) return;
            if (!byId.has(item.storeId)) {
                byId.set(item.storeId, { value: item.storeId, text: item.storeName || item.storeId });
            }
        });
        return Array.from(byId.values());
    }, [everestLogs]);

    const handleResend = async (logId) => {
        try {
            setLoadingResend(logId);

            const res = await api.post('manualEverest', { logId });
            onShowAlert(
                PreAlert.success(
                    res.data?.message || "Sucesso ao reenviar os arquivos"
                )
            );
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.message ||
                "Erro ao reenviar os arquivos";

            onShowAlert(PreAlert.error(message));
            console.error("Erro ao reenviar os arquivos", error);
        } finally {
            setLoadingResend(null);
        }
    };


    const columns = useMemo(() => [
        {
            label: 'Data',
            field: 'date',
            format: row => row.date?.toDate?.().toLocaleString?.('pt-BR') || '',
            isDate: true
        },
        {
            label: 'Loja',
            field: 'storeName',
        },
        {
            label: 'CNPJ',
            field: '{row}',
            format: row => masks.cnpj(row.cnpj),
            formatExport: row => masks.cnpj(row.cnpj)
        },
        {
            label: 'Status',
            field: '{row}',
            format: row => row.success ? 'Sucesso' : 'Falha',
            formatExport: row => row.success ? 'Sucesso' : 'Falha'
        },
        {
            label: 'Ações',
            field: 'actions',
            format: row => (
                <Button
                    color='primary'
                    isLight
                    size='sm'
                    onClick={() => handleResend(row.id)}
                    disabled={loadingResend}
                    icon={loadingResend === row.id ? 'Refresh' : 'Send'}
                >
                    {loadingResend === row.id ? 'Enviando' : 'Reenviar'}
                </Button>
            ),
            noExport: true,
        },
    ], [loadingResend]);

    return (
        <>
            <PageWrapper title={demoPages.integracoes.subMenu.everest.text}>
                <SubHeader>
                    <SubHeaderLeft>
                        <Breadcrumb
                            list={[
                                { title: 'Integrações', to: '/integracoes' },
                                {
                                    title: 'Everest',
                                    to: '/integracoes/everest',
                                },
                            ]}
                        />
                    </SubHeaderLeft>
                    <SubHeaderRight>
                        <Dropdown isOpen={filterMenu} setIsOpen={setFilterMenu}>
                            <DropdownToggle hasIcon={false}>
                                <Button icon='Filter' color='primary' isLight>
                                    Filtros
                                </Button>
                            </DropdownToggle>
                            <DropdownMenu isAlignmentEnd size='lg' isCloseAfterLeave={false}>
                                <div className='container py-2'>
                                    <form className='row g-3' onSubmit={e => { e.preventDefault(); setFilterMenu(false); }}>
                                        <div className='col-12'>
                                            <FormGroup id="estab" label="Estabelecimento">
                                                <Select
                                                    id="estab"
                                                    placeholder="Selecione o estabelecimento"
                                                    list={optionsEstablishments}
                                                    value={selectedEstab || estabSelected || ''}
                                                    onChange={e => setSelectedEstab(e.target.value)}
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className='col-12'>
                                            <FormGroup id="store" label="Loja">
                                                <Select
                                                    id="store"
                                                    placeholder="Selecione a loja"
                                                    list={optionsStores}
                                                    value={selectedStore || ''}
                                                    onChange={e => setSelectedStore(e.target.value)}
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className='col-12'>
                                            <Button
                                                type="button"
                                                color={onlyFailed ? 'success' : 'danger'}
                                                isLight={!onlyFailed}
                                                className='w-100'
                                                onClick={() => setOnlyFailed(prev => !prev)}
                                            >
                                                {onlyFailed ? 'Todas' : 'Somente Falhas'}
                                            </Button>
                                        </div>
                                        <div className='col-6'>
                                            <Button
                                                className='w-100 text-dark bg-white'
                                                color='warning'
                                                isOutline
                                                onClick={() => { setSelectedDate(''); setSelectedEstab(estabSelected || ''); setSelectedStore(''); setOnlyFailed(false); }}
                                            >
                                                Resetar
                                            </Button>
                                        </div>
                                        <div className='col-6'>
                                            <Button className='w-100' color='warning' type='submit'>
                                                Aplicar
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            </DropdownMenu>
                        </Dropdown>
                    </SubHeaderRight>
                </SubHeader>
                <Page container='fluid'>
                    <Card>
                        <CardBody className='table-responsive p-5'>
                            <TableCustom
                                columns={columns}
                                rows={filteredLogs}
                                keyExtractor={row => row.id || `${row.estabId}-${row.storeId}-${row.date?.seconds || Math.random()}`}
                                fileName={"Everest"}
                            />
                        </CardBody>
                    </Card>
                </Page>
            </PageWrapper>
        </>
    );


}

export default Everest
